import { HttpClient } from '@angular/common/http';
import { Component, effect, Input, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  AddProgressBarStack,
  RemoveProgressBarStack,
} from '@app/finxone-web-frontend/app/lib/actions/progress-bar.action';
import { GetProfile } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import { baseCssConfigStyle, KycLevel, ProfileStateModel } from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import SumsubWebSdk from '@sumsub/websdk';
import Handlebars from 'handlebars';
import { debounceTime, map, Observable, Subject, takeUntil, timer } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { RoleState, RoleStateModel } from '../../../../lib/state/role.state';
import { BaseComponent } from '../../../../Modules/shared/widgets/base/base.component';

// Type for the onApplicantActionCompleted event payload
interface ApplicantActionCompletedPayload {
  action: string;
  applicantActionId: string;
  answer: string;
}

@Component({
  standalone: false,
  selector: 'app-kyc-provider',
  templateUrl: './kyc-provider.component.html',
  styleUrls: ['./kyc-provider.component.scss'],
})
export class KycProviderComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() public contentConfigStyle: baseCssConfigStyle;
  @Input() public applicationCompleteMessage: string;

  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  @Select(ProfileState.getProfile)
  profile!: Observable<ProfileStateModel>;
  roleInfo: RoleStateModel;
  urlParams: string[] = [];
  roleName: string;
  subscription: string;
  accessToken: string;
  kycEmail: string;
  kycPhone: string;
  colorString = '';
  kycLevel: KycLevel;
  showApplicationCompleteMessage = false;
  sumsubCompleteEvent = new Subject<void>();
  construct = 'true';
  public renderedContent = '';
  private isOnboarding = true;
  constructor(
    private configService: ConfigService,
    private _router: Router,
    private metadataService: MetadataService,
    private http: HttpClient,
    private store: Store,
    private route: ActivatedRoute,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super();
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params: ParamMap) => {
      const sumsubAccessToken = params.get('sumsubAccessToken');
      if (sumsubAccessToken) {
        this.isOnboarding = false;
        this.accessToken = sumsubAccessToken;
      } else {
        this.kycLevel = params.get('type') as KycLevel;
      }
    });
    this.roles.subscribe((roles) => {
      this.roleInfo = roles;
    });
    effect(() => {
      const globalCssVars = this.configService.rawGlobalCssVariables;
      if (globalCssVars) {
        this.colorString = globalCssVars();
      }
    });
  }
  ngOnInit() {
    this.onRenderHandleBarText();

    const urlParams = window.location.pathname;
    const params = urlParams.split('/');
    params.splice(0, 3);
    this.urlParams = params;
    this.roleName = this.urlParams[0];
    this.subscription = this.urlParams[1];

    if (this.isOnboarding) {
      this.getNewAccessToken();
      // Once sumsub completes we begin the process of calling get profile api every 10 seconds
      this.sumsubCompleteEvent.pipe(debounceTime(2000)).subscribe(() => {
        timer(10000, 10000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.store.dispatch(new GetProfile(false));
          });
      });
      this.profile.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
        // once the profile gets updated from the get profile apis calls above kyc should go to 'success'
        // at that point we can send the user to the landing page
        const individualKycSuccessAndNoKYB =
          profile.kycVerificationStatus === 'success' && profile.activeOrganisationKybStatus === '';

        if (individualKycSuccessAndNoKYB || profile.activeOrganisationKybStatus === 'success') {
          this.navigateToApp();
        }
      });
    } else {
      //unsure what email and phone number will be used for this
      this.launchWebSdk(this.accessToken, this.kycEmail, this.kycPhone, 'Test');

      this.sumsubCompleteEvent.subscribe(() => {
        //naviate to account created page
      });
    }
  }

  private onRenderHandleBarText() {
    if (!this.applicationCompleteMessage) {
      return;
    }
    const template = Handlebars.compile(this.applicationCompleteMessage);

    buildHandleBarModel(
      this.store,
      this.applicationCompleteMessage,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  launchWebSdk(accessToken: string, applicantEmail: string, applicantPhone: string, customI18nMessages: any) {
    const snsWebSdkInstance = SumsubWebSdk.init(
      accessToken,
      // token update callback, must return Promise
      // Access token expired
      // get a new one and pass it to the callback to re-initiate the WebSDK
      () => this.getPromiseAccessToken(),
    )
      .withConf({
        lang: 'en', //language of WebSDK texts and comments (ISO 639-1 format)
        email: applicantEmail,
        phone: applicantPhone,
        i18n: customI18nMessages, //JSON of custom SDK Translations
        uiConf: {
          customCssStr:
            ':root {\n  ' +
            this.colorString +
            '\n} p {color: var(--color-on-background);font-size: 16px;line-height: 24px;}section {margin: 40px auto;}input {color: var(--color-on-background-muted);font-weight: 600;outline: none;}section.content {background-color: var(--color-background);color: var(--color-on-background);padding: 40px 40px 16px;box-shadow: none;border-radius: 6px;}button.submit,button.back {text-transform: capitalize;border-radius: 6px;height: 48px;padding: 0 30px;font-size: 16px;background-image: none !important;transform: none !important;box-shadow: none !important;transition: all 0.2s linear;}button.submit {min-width: 132px;background: none;background-color: var(--color-primary);color: var(--color-on-surface) }button.submit.disabled {background-color: var(--color-primary);color: var(--color-on-surface);border: 1px solid var(--color-background);}.round-icon {background-color: var(--color-border-color) !important;background-image: none !important;}body, body.loaded {background-color: var(--color-background);}h3,h4 {color: var(--color-on-background);}.step.active .title {color: var(--color-on-background);}.step .title {color: var(--color-on-background);}.radio-item {color: var(--color-on-background);}.sumsub-logo {color: var(--color-on-background);}ul li {color: var(--color-on-background);}.mobile-button {background-color: var(--color-background);}.mobile-button .fa-icon {color: var(--color-on-background);}.upload-item .drag-drop {background-color: var(--color-background);}.upload-payment-item .upload-item{border: 1px solid var(--color-background);background-color: var(--color-background);}.completed {color:var(--color-on-background);}.review-complete, .review-done {color:var(--color-on-background);}',
        },
      })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onApplicantStatusChanged', (payload) => {
        console.log(payload);
        if (payload.reviewStatus == 'completed') {
          this.showApplicationCompleteMessage = true;
          this.sumsubCompleteEvent.next();
        }
      })
      // see below what kind of messages WebSDK generates
      .on('idCheck.applicantStatus', (payload) => {
        console.log(payload);
        if (payload.reviewStatus == 'completed') {
          this.showApplicationCompleteMessage = true;
          this.sumsubCompleteEvent.next();
        }
      })
      .on('idCheck.onError', (error) => {
        console.log('onError', error);
      })
      .on('idCheck.onApplicantLoaded', (payload) => {
        //Don't call SetApplicant Data when verification is for only KYB.
        if (this.kycLevel == KycLevel.BUSINESS) {
          this.setCompanyInfo(payload.applicantId);
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on('idCheck.onApplicantActionCompleted', (payload: ApplicantActionCompletedPayload) => {
        console.log(payload);
        setTimeout(() => {
          this.navigateToApp();
        }, 4000);
      })
      .on('idCheck.onInitialized', () => {
        this.store.dispatch(new RemoveProgressBarStack({ uniqueId: 'getNewAccessToken' }));
      })
      .build();

    // you are ready to go:
    // just launch the WebSDK by providing the container element for it
    snsWebSdkInstance.launch('#sumsub-websdk-container');
  }

  setCompanyInfo(applicantId: string) {
    this.construct = localStorage.getItem('loadApplicant') ?? 'true';
    return this.metadataService
      .setCompanyInfo(applicantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (this.construct === 'true') {
          localStorage.setItem('loadApplicant', 'false');
          this.launchWebSdk(this.accessToken, this.kycEmail, this.kycPhone, 'Test');
        }
      });
  }

  private getNewAccessToken() {
    this.store.dispatch(new AddProgressBarStack({ uniqueId: 'getNewAccessToken' }));
    // Add KYC Level business if you want to use businees KYC i.e.KycLevel.BUSINESS
    return this.metadataService
      .getKycAccessToken(this.kycLevel)
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          if (response?.token) {
            this.accessToken = response.token;
            this.kycEmail = response.email;
            this.kycPhone = response.phoneNumber;
            this.launchWebSdk(this.accessToken, this.kycEmail, this.kycPhone, 'Test');
          }
        }),
      )
      .subscribe();
  }

  getPromiseAccessToken() {
    return Promise.resolve('');
  }

  private navigateToApp() {
    localStorage.removeItem('loadApplicant');
    this._router.navigateByUrl('/zones/landing/home');
  }
}
